import React from "react"
import { ChevronDown } from "lucide-react"

interface MetaAdAccount {
  id: string
  accountId: string
  accountName: string
  businessId?: string | null
  isPrimary: boolean
  isActive: boolean
  lastSyncAt?: Date | null
  company?: {
    id: string
    name: string
    slug: string
    color?: string | null
  }
}

interface AccountSelectorProps {
  accounts: MetaAdAccount[]
  selectedAccountId?: string
  onAccountChange: (accountId: string) => void
}

export function AccountSelector({ accounts, selectedAccountId, onAccountChange }: AccountSelectorProps) {
  const [isOpen, setIsOpen] = React.useState(false)

  const selectedAccount = accounts.find((acc) => acc.accountId === selectedAccountId)

  if (accounts.length === 0) {
    return null
  }

  if (accounts.length === 1) {
    const account = accounts[0]
    return (
      <div className="bg-card rounded-lg border border-border p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-medium text-card-foreground">{account.accountName}</h3>
              {account.company && <span className="text-xs text-muted-foreground">• {account.company.name}</span>}
            </div>
            <p className="text-sm text-muted-foreground">Account ID: {account.accountId}</p>
            {account.isPrimary && (
              <span className="inline-block bg-primary/10 text-primary text-xs px-2 py-1 rounded-full mt-1">
                Primary
              </span>
            )}
          </div>
          <div className="text-right">
            <div className="w-2 h-2 bg-green-500 rounded-full mb-1"></div>
            <p className="text-xs text-muted-foreground">Connected</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-card rounded-lg border border-border p-4 text-left hover:bg-accent transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {selectedAccount ? (
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-medium text-card-foreground">{selectedAccount.accountName}</h3>
                  {selectedAccount.company && (
                    <span className="text-xs text-muted-foreground">• {selectedAccount.company.name}</span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground">Account ID: {selectedAccount.accountId}</p>
                {selectedAccount.isPrimary && (
                  <span className="inline-block bg-primary/10 text-primary text-xs px-2 py-1 rounded-full mt-1">
                    Primary
                  </span>
                )}
              </div>
            ) : (
              <div>
                <h3 className="font-medium text-card-foreground">Select Account</h3>
                <p className="text-sm text-muted-foreground">Choose a Meta Ads account</p>
              </div>
            )}
          </div>

          <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </div>
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />

          {/* Dropdown */}
          <div className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-lg shadow-lg z-20 max-h-64 overflow-y-auto">
            {accounts.map((account) => (
              <button
                key={account.id}
                onClick={() => {
                  onAccountChange(account.accountId)
                  setIsOpen(false)
                }}
                className={`w-full p-4 text-left hover:bg-accent transition-colors border-b border-border last:border-b-0 ${
                  account.accountId === selectedAccountId ? "bg-accent" : ""
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-popover-foreground">{account.accountName}</h4>
                      {account.company && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-secondary-foreground">
                          {account.company.name}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">ID: {account.accountId}</p>
                    {account.businessId && (
                      <p className="text-xs text-muted-foreground">Business: {account.businessId}</p>
                    )}

                    <div className="flex items-center gap-2 mt-2">
                      {account.isPrimary && (
                        <span className="inline-block bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">
                          Primary
                        </span>
                      )}

                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-xs text-muted-foreground">Connected</span>
                      </div>
                    </div>

                    {account.lastSyncAt && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Last sync: {new Date(account.lastSyncAt).toLocaleDateString()}
                      </p>
                    )}
                  </div>

                  {account.accountId === selectedAccountId && (
                    <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                      <div className="w-2 h-2 bg-white rounded-full"></div>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
